import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { ButtonStyledLikeLink, Icon, Link, Spinner } from 'xm-spark';

import NoResults from 'resources/svg/no-results.svg';
import useDebounce from 'hooks/useDebounce';
import { post } from '@xmatters/redux/services/httpService';

import useHelpSearch from './useHelpSearch';
import styles from './HelpSearch.module.scss';

const HELP_URL = 'https://help.xmatters.com/';

function SearchResultTitleLink({
  children,
  hideResults,
  isExternal,
  result,
  setHelpLocation,
}) {
  if (isExternal) {
    return (
      <Link href={result.url} className={styles.resultLink} target="_blank">
        {children}
      </Link>
    );
  }
  return (
    <ButtonStyledLikeLink
      onClick={() => {
        setHelpLocation(result.url);
        hideResults();
      }}
      className={styles.resultLink}
    >
      {children}
    </ButtonStyledLikeLink>
  );
}

SearchResultTitleLink.displayName = 'SearchResultTitleLink';
SearchResultTitleLink.propTypes = {
  children: PropTypes.node.isRequired,
  isExternal: PropTypes.bool.isRequired,
  result: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  setHelpLocation: PropTypes.func.isRequired,
};

function isResultExternal(result) {
  const { url } = result;
  if (!url?.startsWith(HELP_URL)) {
    // Anything that doesn't start with "https://help.xmatters.com/" (or EBFD equivalent) is treated as external:
    return true;
  }

  if (url.toLowerCase().includes('/workflows/appendixrestapi.htm')) {
    // COR-67824 - By default treat "Deprecated REST API methods" page as external:
    return true;
  }

  // xm-api docs are treated as external, even though they're under help.xmatters.com:
  return /https?:\/\/help.xmatters.com\/xmapi/i.test(url);
}

function SearchResults({
  hideResults,
  isLoading,
  onClear,
  resultsData,
  setHelpLocation,
}) {
  if (resultsData) {
    return (
      <div className={styles.searchResults}>
        {!resultsData.length && (
          <div className={styles.noResults}>
            <NoResults />
            <FormattedMessage id="NO_MATCHING_RESULTS" tagName="div" />
            <ButtonStyledLikeLink
              className={styles.noResultsLink}
              onClick={onClear}
            >
              <FormattedMessage id="START_OVER" />
            </ButtonStyledLikeLink>
          </div>
        )}
        {resultsData.map(result => {
          const isExternal = isResultExternal(result);
          return (
            <div key={result.url} className={styles.searchRow}>
              <SearchResultTitleLink
                hideResults={hideResults}
                isExternal={isExternal}
                result={result}
                setHelpLocation={setHelpLocation}
              >
                <img src={result.image} alt="" />
                <span className={styles.title}>{result.title}</span>
                {isExternal && <Icon type="icon-link-widget" />}
              </SearchResultTitleLink>
              <div className={styles.resultBody} title={result.body}>
                {result.body}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    isLoading && (
      <div className={styles.searchSpinner}>
        <Spinner />
      </div>
    )
  );
}

SearchResults.displayName = 'SearchResults';
SearchResults.propTypes = {
  hideResults: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  setHelpLocation: PropTypes.func.isRequired,
  resultsData: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
};
SearchResults.defaultProps = {
  resultsData: null,
};

export default function XmattersHelpSearch({ contentRef, setHelpLocation }) {
  const resultsRef = useRef();
  const queryRef = useRef();
  const [isActive, setIsActive] = useState(false);
  const hideResults = useCallback(() => setIsActive(false), [setIsActive]);

  const [results, setResults] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const resultsData = results?.records?.page;

  const requestData = useCallback(
    requestQuery => {
      post({
        uri: 'https://search-api.swiftype.com/api/v1/public/installs/ZT685gokyiU3zzzfByq2/suggest.json',
        params: { q: requestQuery },
      })
        .then(data => {
          if (requestQuery === queryRef.current) {
            setResults(data);
          }
        })
        .catch((...args) => {
          // TODO handle error
          // eslint-disable-next-line no-console
          console.error('ERROR SEARCHING ', args);
        })
        .then(() => {
          if (requestQuery === queryRef.current) {
            setIsLoading(false);
          }
        });
    },
    [setIsLoading, setResults],
  );

  const doSearch = useDebounce(requestData, 150);

  const { searchComponent, onClear, query } = useHelpSearch({
    onChange: newQuery => {
      queryRef.current = newQuery;
      setIsLoading(true);
      setIsActive(!!newQuery);
      doSearch(newQuery);
    },
    onFocus: e => {
      setIsActive(!!e.target.value);
    },
  });

  return (
    <>
      {searchComponent}
      {isActive &&
        query &&
        ReactDOM.createPortal(
          <div ref={resultsRef}>
            <SearchResults
              hideResults={hideResults}
              isLoading={isLoading}
              onClear={onClear}
              query={query}
              resultsData={resultsData}
              setHelpLocation={setHelpLocation}
            />
          </div>,
          contentRef.current,
        )}
    </>
  );
}

XmattersHelpSearch.displayName = 'XmattersHelpSearch';
XmattersHelpSearch.propTypes = {
  contentRef: PropTypes.object.isRequired,
  setHelpLocation: PropTypes.func.isRequired,
};
